import { useState } from 'react';

const concerns = [
  {
    icon: 'ri-emotion-sad-line',
    title: '감정 소진 & 번아웃',
    desc: '장기간 돌봄 업무로 인한 감정적 소진과 번아웃을 경험하는 활동지원사가 많습니다.',
    count: '전체의 68%',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: '처우 및 급여 문제',
    desc: '적절한 보수와 근로 조건이 보장되지 않아 어려움을 호소하는 사례가 지속됩니다.',
    count: '전체의 54%',
  },
  {
    icon: 'ri-shield-user-line',
    title: '안전 및 인권 침해',
    desc: '업무 중 발생하는 안전 위협과 인권 침해 사례에 대한 체계적 대응이 필요합니다.',
    count: '전체의 43%',
  },
  {
    icon: 'ri-user-heart-line',
    title: '이용자와의 관계',
    desc: '장애인 이용자 및 가족과의 관계에서 경계 설정과 소통에 어려움을 겪는 경우가 많습니다.',
    count: '전체의 61%',
  },
];

export default function CounselingSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    category: '',
    message: '',
    agree: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.agree) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('name', formData.name);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      params.append('category', formData.category);
      params.append('message', formData.message);
      await fetch('https://readdy.ai/api/form/d70f4bf9hgr6oot2qu6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="counseling" className="py-28 bg-stone-800">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-500/30 text-amber-400 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <i className="ri-ear-line"></i>
            SUPPORT & COUNSELING
          </div>
          <h2 className="text-5xl font-bold text-white mb-4">
            활동지원사의 목소리에<br />
            <span className="text-amber-400">귀 기울입니다</span>
          </h2>
          <p className="text-stone-400 text-lg max-w-xl mx-auto">
            고충을 혼자 감당하지 마세요. 전문 상담사가 함께 해결책을 찾아드립니다.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {concerns.map((c) => (
            <div key={c.title} className="bg-stone-700/50 rounded-2xl p-6 border border-stone-600 hover:border-amber-500/50 transition-colors">
              <div className="w-11 h-11 flex items-center justify-center bg-amber-500/20 rounded-xl mb-4">
                <i className={`${c.icon} text-amber-400 text-xl`}></i>
              </div>
              <h3 className="text-white font-semibold mb-2">{c.title}</h3>
              <p className="text-stone-400 text-sm leading-relaxed mb-4">{c.desc}</p>
              <div className="bg-amber-600/20 rounded-lg px-3 py-1.5 inline-block">
                <span className="text-amber-400 text-xs font-semibold">{c.count} 경험</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h3 className="text-3xl font-bold text-white mb-6">전문 상담 신청</h3>
            <p className="text-stone-400 leading-relaxed mb-8">
              활동지원사로서 업무 중 어려운 점이 있으신가요? 심리 상담, 노무 상담, 인권 상담 등 분야별
              전문가가 1:1로 도움을 드립니다. 모든 상담 내용은 철저히 비밀이 보장됩니다.
            </p>
            <div className="space-y-4">
              {[
                { icon: 'ri-phone-line', text: '평일 09:00 - 18:00 전화 상담', sub: '032-511-0012', href: 'tel:032-511-0012' },
                { icon: 'ri-kakao-talk-line', text: '카카오톡 채널 상담', sub: '@대한효복지센터', href: 'http://pf.kakao.com/_shtEX' },
                { icon: 'ri-map-pin-line', text: '방문 상담 (예약 필수)', sub: '인천시 부평구 원적로 290 산곡메디칼', href: null },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-stone-700 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-amber-400`}></i>
                  </div>
                  <div>
                    <p className="text-stone-300 text-sm font-medium">{item.text}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-amber-400 text-sm hover:text-amber-300 transition-colors cursor-pointer"
                      >
                        {item.sub}
                      </a>
                    ) : (
                      <p className="text-amber-400 text-sm">{item.sub}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <a
              href="http://pf.kakao.com/_shtEX"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 flex items-center justify-center gap-3 w-full py-4 rounded-2xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#FEE500', color: '#191919' }}
            >
              <i className="ri-kakao-talk-fill text-xl"></i>
              카카오톡 채널 바로 상담하기
            </a>
          </div>

          <div className="bg-stone-700/30 rounded-3xl p-8 border border-stone-600">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-green-400 text-3xl"></i>
                </div>
                <h4 className="text-white font-bold text-xl mb-2">상담 신청이 완료되었습니다</h4>
                <p className="text-stone-400">담당 상담사가 1-2 영업일 내로 연락드릴 예정입니다.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <h4 className="text-white font-bold text-lg mb-6">상담 신청서</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-stone-400 text-sm mb-1.5 block">이름 *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="홍길동"
                      className="w-full bg-stone-800 border border-stone-600 rounded-xl px-4 py-3 text-white text-sm placeholder:text-stone-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="text-stone-400 text-sm mb-1.5 block">연락처 *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="010-0000-0000"
                      className="w-full bg-stone-800 border border-stone-600 rounded-xl px-4 py-3 text-white text-sm placeholder:text-stone-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1.5 block">이메일</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    className="w-full bg-stone-800 border border-stone-600 rounded-xl px-4 py-3 text-white text-sm placeholder:text-stone-500 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1.5 block">상담 유형 *</label>
                  <select
                    name="category"
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-stone-800 border border-stone-600 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-500"
                  >
                    <option value="">상담 유형을 선택해 주세요</option>
                    <option value="심리 상담">심리 상담 (번아웃, 스트레스)</option>
                    <option value="노무 상담">노무 상담 (급여, 근무 조건)</option>
                    <option value="인권 상담">인권 상담 (안전, 권리 침해)</option>
                    <option value="관계 상담">이용자 관계 상담</option>
                    <option value="기타">기타</option>
                  </select>
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1.5 block">고충 내용 *</label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    placeholder="어떤 어려움을 겪고 계신지 자유롭게 작성해 주세요. (최대 500자)"
                    rows={4}
                    className="w-full bg-stone-800 border border-stone-600 rounded-xl px-4 py-3 text-white text-sm placeholder:text-stone-500 focus:outline-none focus:border-amber-500 resize-none"
                  />
                  <p className="text-stone-500 text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="agree"
                    checked={formData.agree}
                    onChange={(e) => setFormData({ ...formData, agree: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-amber-500 cursor-pointer"
                  />
                  <label htmlFor="agree" className="text-stone-400 text-sm cursor-pointer">
                    개인정보 수집 및 이용에 동의합니다. 수집된 정보는 상담 목적으로만 사용됩니다.
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={submitting || !formData.agree}
                  className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 disabled:bg-stone-600 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '제출 중...' : '상담 신청하기'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
